import { useState } from 'react';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { Button } from '@/components/base/buttons/button';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import type { QuoteRule, CreateQuoteRuleData, UpdateQuoteRuleData } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { X, Plus } from '@untitledui/icons';

interface QuoteRuleFormProps {
    rule?: QuoteRule;
    onSubmit: (data: CreateQuoteRuleData | UpdateQuoteRuleData) => void;
    onCancel: () => void;
}

export const QuoteRuleForm = ({ rule, onSubmit, onCancel }: QuoteRuleFormProps) => {
    const { t } = useTranslation('pages:quotes');
    const [formData, setFormData] = useState({
        name: rule?.name || '',
        description: rule?.description || '',
        naturalLanguageRule: rule?.naturalLanguageRule || '',
        autoResponseConditions: rule?.autoResponseConditions || [''],
        isActive: rule?.isActive !== undefined ? rule.isActive : true,
    });

    const handleAddCondition = () => {
        setFormData({
            ...formData,
            autoResponseConditions: [...formData.autoResponseConditions, ''],
        });
    };

    const handleRemoveCondition = (index: number) => {
        const newConditions = formData.autoResponseConditions.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            autoResponseConditions: newConditions.length > 0 ? newConditions : [''],
        });
    };

    const handleConditionChange = (index: number, value: string) => {
        const newConditions = [...formData.autoResponseConditions];
        newConditions[index] = value;
        setFormData({
            ...formData,
            autoResponseConditions: newConditions,
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const conditions = formData.autoResponseConditions.filter(c => c.trim() !== '');
        onSubmit({
            ...formData,
            autoResponseConditions: conditions.length > 0 ? conditions : [],
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Input
                label={t('settings.rules.form.name')}
                placeholder={t('settings.rules.form.namePlaceholder')}
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                isRequired
                size="md"
            />

            <TextArea
                label={t('settings.rules.form.description')}
                placeholder={t('settings.rules.form.descriptionPlaceholder')}
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })}
                rows={3}
            />

            <TextArea
                label={t('settings.rules.form.naturalLanguageRule')}
                placeholder={t('settings.rules.form.naturalLanguageRulePlaceholder')}
                value={formData.naturalLanguageRule}
                onChange={(value) => setFormData({ ...formData, naturalLanguageRule: value })}
                isRequired
                rows={4}
                hint={t('settings.rules.form.naturalLanguageRuleHint')}
            />

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-primary">
                        {t('settings.rules.form.autoResponseConditions')}
                    </label>
                    <Button
                        type="button"
                        onClick={handleAddCondition}
                        color="secondary"
                        size="sm"
                        iconLeading={Plus}
                    >
                        {t('settings.rules.form.addCondition')}
                    </Button>
                </div>
                <div className="space-y-2">
                    {formData.autoResponseConditions.map((condition, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <Input
                                placeholder={t('settings.rules.form.conditionPlaceholder')}
                                value={condition}
                                onChange={(value) => handleConditionChange(index, value)}
                                size="md"
                                className="flex-1"
                            />
                            {formData.autoResponseConditions.length > 1 && (
                                <Button
                                    type="button"
                                    onClick={() => handleRemoveCondition(index)}
                                    color="primary-destructive"
                                    size="sm"
                                    iconLeading={X}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <Checkbox
                isSelected={formData.isActive}
                onChange={(isSelected) => setFormData({ ...formData, isActive: isSelected })}
                label={t('settings.rules.form.isActive')}
                hint={t('settings.rules.form.isActiveLabel')}
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary">
                <Button
                    type="button"
                    onClick={onCancel}
                    color="secondary"
                    size="md"
                >
                    {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                >
                    {rule ? t('buttons.update', { ns: 'common' }) : t('buttons.create', { ns: 'common' })}
                </Button>
            </div>
        </form>
    );
};

